import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import SectionHeading from '../ui/SectionHeading';
import { ArrowRight, Quote, Star } from '../ui/Icons';

/**
 * Testimonials previously rendered product banner images as if they were
 * customer photos. Since we have no photographs of these customers, the cards
 * now use a lettered monogram — cleaner, and honest about what we have.
 */
const reviews = [
  {
    name: 'Shivanshi Swain',
    location: 'Bhubaneswar',
    project: '3BHK interior repaint',
    review: 'The team helped us settle on a warm neutral for the living room and the finish is still flawless a year on. Genuinely happy with the work.',
    rating: 5,
  },
  {
    name: 'RajNandini Das',
    location: 'Cuttack',
    project: 'Exterior elevation',
    review: 'Our façade took a beating every monsoon. Two seasons after the Vertex exterior coat, there is still no damp patch or peeling.',
    rating: 5,
  },
  {
    name: 'Pritam Pattnayak',
    location: 'Puri',
    project: 'Villa — full home',
    review: "Site was left spotless every evening. The colour consultant's suggestions were far better than what we had originally picked.",
    rating: 5,
  },
  {
    name: 'Vaishnavi Bharadwaj',
    location: 'Rourkela',
    project: 'Bedroom & study',
    review: 'Good coverage in two coats and almost no smell, so the rooms were usable the same week. Reasonable on price too.',
    rating: 4,
  },
  {
    name: 'Pritish Dhal',
    location: 'Sambalpur',
    project: 'Duplex repaint',
    review: 'The cost calculator matched the final quote closely, which made planning the budget much easier. Would use them again.',
    rating: 5,
  },
];

const AVERAGE_RATING = 4.8;

const initials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('');

const Stars = ({ rating, size = 15 }: { rating: number; size?: number }) => (
  <div className="flex gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} className={i < Math.round(rating) ? 'text-brand-gold' : 'text-brand-line'} />
    ))}
  </div>
);

const CustomerReviews = () => (
  <section className="vp-section bg-white">
    <div className="container">
      <SectionHeading
        eyebrow="Testimonials"
        title="What our customers say"
        lead="Feedback from homeowners who have lived with the finish for a season or more."
        className="mb-10"
        action={
          <div className="flex flex-col sm:flex-row lg:flex-col lg:items-end gap-3">
            {/* Google-style rating badge */}
            <div className="inline-flex items-center gap-3 rounded-2xl border border-brand-line bg-brand-surface px-4 py-3 shadow-soft">
              <span className="text-3xl font-bold text-brand-ink leading-none tabular-nums">{AVERAGE_RATING.toFixed(1)}</span>
              <span className="flex flex-col gap-1">
                <Stars rating={AVERAGE_RATING} size={14} />
                <span className="text-xs text-brand-muted">Average rating out of 5</span>
              </span>
            </div>
            <Link to="/testimonials" className="vp-link text-sm self-start lg:self-end">
              Read more testimonials
              <ArrowRight size={14} />
            </Link>
          </div>
        }
      />

      <Swiper
        modules={[Pagination, Autoplay]}
        slidesPerView={3}
        spaceBetween={24}
        loop={true}
        autoplay={{ delay: 4500, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        className="!pb-14"
        breakpoints={{
          320: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
        }}
      >
        {reviews.map((review) => (
          <SwiperSlide key={review.name} className="h-auto">
            <figure className="h-full flex flex-col bg-white rounded-2xl p-6 lg:p-7 border border-brand-line shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-500 ease-premium">
              <Quote size={30} className="text-brand-blue/15" />

              <div className="mt-3">
                <Stars rating={review.rating} />
              </div>

              <blockquote className="mt-4 text-[15px] text-brand-ink/85 leading-relaxed flex-1">{review.review}</blockquote>

              <figcaption className="mt-6 pt-5 border-t border-brand-line flex items-center gap-3.5">
                <span className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-brand-blue to-brand-red text-white font-semibold text-sm flex items-center justify-center" aria-hidden="true">
                  {initials(review.name)}
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold text-brand-ink text-sm truncate">{review.name}</span>
                  <span className="block text-xs text-brand-muted truncate">
                    {review.project} · {review.location}
                  </span>
                </span>
              </figcaption>
            </figure>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  </section>
);

export default CustomerReviews;
